"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import {
  dictionaries,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type Dictionary,
  type Locale,
} from "./dictionaries"

const LOCALE_STORAGE_KEY = "agenthub.locale"
const LOCALE_COOKIE = "agenthub-locale"

interface I18nContextValue {
  locale: Locale
  t: Dictionary
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18nContextValue | null>(null)

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
      return stored as Locale
    }
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE)

  // Hydrate from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    const initial = readInitialLocale()
    if (initial !== locale) {
      setLocaleState(initial)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setLocale = (next: Locale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next)
      document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Lax`
    } catch {
      // ignore
    }
  }

  const value: I18nContextValue = {
    locale,
    t: dictionaries[locale],
    setLocale,
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useTranslation() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    // Fallback so server components or unwrapped trees don't crash
    return {
      locale: DEFAULT_LOCALE,
      t: dictionaries[DEFAULT_LOCALE],
      setLocale: () => {},
    }
  }
  return ctx
}
